import { useEffect, useState } from "react";
import { getSignedPhotoUrl, getSignedPhotoUrls } from "@/lib/storageUtils";

interface SignedImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  storedUrl: string | null | undefined;
}

/**
 * Renders an image from a stored photo URL/path by generating a signed URL.
 * Shows nothing if storedUrl is null/undefined.
 */
export function SignedImage({ storedUrl, alt, ...props }: SignedImageProps) {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!storedUrl) {
      setSignedUrl(null);
      return;
    }
    let cancelled = false;
    getSignedPhotoUrl(storedUrl).then((url) => {
      if (!cancelled) setSignedUrl(url);
    });
    return () => { cancelled = true; };
  }, [storedUrl]);

  if (!signedUrl) return null;

  return <img src={signedUrl} alt={alt || ""} {...props} />;
}

/**
 * Hook to get a signed URL for a single photo.
 */
export function useSignedUrl(storedUrl: string | null | undefined): string | null {
  const [signedUrl, setSignedUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!storedUrl) { setSignedUrl(null); return; }
    let cancelled = false;
    getSignedPhotoUrl(storedUrl).then((url) => {
      if (!cancelled) setSignedUrl(url);
    });
    return () => { cancelled = true; };
  }, [storedUrl]);

  return signedUrl;
}

/**
 * Hook to get signed URLs for an array of photos.
 */
export function useSignedUrls(storedUrls: string[]): string[] {
  const [signedUrls, setSignedUrls] = useState<string[]>([]);

  useEffect(() => {
    if (storedUrls.length === 0) { setSignedUrls([]); return; }
    let cancelled = false;
    getSignedPhotoUrls(storedUrls).then((urls) => {
      if (!cancelled) setSignedUrls(urls);
    });
    return () => { cancelled = true; };
  }, [storedUrls.join(",")]);

  return signedUrls;
}
